import type { components as CmsComponents } from '@lifeserver/models/cms';
import type { components as GiftComponents } from '@lifeserver/models/gift';
import { groupBy } from 'lodash';

import { getCalendarDateParts, isValidDate } from '../utils/dates';

import { findGiftForSubtask } from './giftPlan';

type Gift = GiftComponents['schemas']['FullGiftAdminItem'];
type JiraIssueSummary = CmsComponents['schemas']['JiraIssueSummary'];
export type JiraSubtask = CmsComponents['schemas']['JiraSubtask'];

export interface ScheduledGiftOccurrence {
    gift: Gift;
    publicationId: string;
    startDate: string;
    endDate: string | null;
}

export interface GiftSchedule {
    allSubtasks: JiraSubtask[];
    plannedGifts: JiraSubtask[];
    years: number[];
}

export interface GiftScheduleYear {
    giftsByMonth: Record<number, ScheduledGiftOccurrence[]>;
    plannedByMonth: Record<number, JiraSubtask[]>;
    totalGifts: number;
    totalPlanned: number;
}

const groupByMonth = <Item>(
    items: readonly Item[],
    getDate: (item: Item) => string | null | undefined,
) =>
    groupBy(
        items.filter((item) => getCalendarDateParts(getDate(item))),
        (item) => getCalendarDateParts(getDate(item))?.month,
    );

const getOccurrences = (gift: Gift): ScheduledGiftOccurrence[] =>
    gift.publications.map((publication) => ({
        gift,
        publicationId: publication.id,
        startDate: publication.startDate,
        endDate: publication.endDate,
    }));

/** Combines Jira responses and derives the calendar's planned-gift view. */
export const createGiftSchedule = (
    gifts: readonly Gift[] | undefined,
    issues: readonly JiraIssueSummary[],
): GiftSchedule => {
    const seen = new Set<string>();
    const allSubtasks = issues
        .flatMap((issue) => issue.subtasks ?? [])
        .filter((subtask) => {
            if (seen.has(subtask.key)) return false;
            seen.add(subtask.key);
            return true;
        });
    const plannedGifts = allSubtasks.filter(
        (subtask) =>
            isValidDate(subtask.startTime) &&
            !findGiftForSubtask(gifts, subtask),
    );
    const occurrences = (gifts ?? []).flatMap(getOccurrences);
    const years = [
        ...occurrences.map((occurrence) =>
            getCalendarDateParts(occurrence.startDate),
        ),
        ...plannedGifts.map((subtask) =>
            getCalendarDateParts(subtask.startTime),
        ),
    ]
        .flatMap((dateParts) => (dateParts ? [dateParts.year] : []))
        .toSorted((first, second) => second - first)
        .filter(
            (year, index, values) => index === 0 || values[index - 1] !== year,
        );

    return { allSubtasks, plannedGifts, years };
};

/** Buckets scheduled and planned gifts for one calendar year. */
export const createGiftScheduleYear = (
    gifts: readonly Gift[] | undefined,
    plannedGifts: readonly JiraSubtask[],
    year: number,
): GiftScheduleYear => {
    const giftsForYear = (gifts ?? [])
        .flatMap(getOccurrences)
        .filter(
            (occurrence) =>
                getCalendarDateParts(occurrence.startDate)?.year === year,
        );
    const plannedForYear = plannedGifts.filter(
        (subtask) => getCalendarDateParts(subtask.startTime)?.year === year,
    );

    return {
        giftsByMonth: groupByMonth(
            giftsForYear,
            (occurrence) => occurrence.startDate,
        ),
        plannedByMonth: groupByMonth(
            plannedForYear,
            (subtask) => subtask.startTime,
        ),
        totalGifts: giftsForYear.length,
        totalPlanned: plannedForYear.length,
    };
};
